/* eslint-disable no-shadow */
import { createSelector } from '@reduxjs/toolkit';
import { MEMBER_PLAN_SLICE_NAME } from 'store/slices/slicesNames';
import { RootState } from 'store/store';
import { type CoverageSnapshot, type Amounts } from 'schemas';
import {
  getPlanAmounts,
  getPlanConfigAccumulations,
  getRemaining,
  getFiniteNumber,
  isFiniteNumber,
} from './memberPlanUtils';
import {
  DEDUCTIBLE_AMOUNT_KEY,
  FAMILY_PLAN_LEVEL,
  INDIVIDUAL_PLAN_LEVEL,
  OOP_AMOUNT_KEY,
} from './memberSliceConstants';
import * as selectUi from '../ui/selectUI';
import { PlanConfig } from './memberPlanSlice';

export interface PlanAmounts {
  individualOutOfPocketMax: number;
  // certain plans have no deductible, allow undefined
  individualDeductible: number | undefined;
  // family values may or may not be present
  familyOutOfPocketMax: number | undefined;
  familyDeductible: number | undefined;
  // all accumulations default to 0
  individualDeductibleAccumulation: number;
  individualOutOfPocketMaxAccumulation: number;
  familyOutOfPocketMaxAccumulation: number;
  familyDeductibleAccumulation: number;
}

export interface PlanAmountsRemaining {
  individualOutOfPocketMaxRemaining: number | undefined;
  individualDeductibleRemaining: number | undefined;
  familyOutOfPocketMaxRemaining: number | undefined;
  familyDeductibleRemaining: number | undefined;
}

/** ************ ************** */
/** Root level state properties */
/** ************ ************** */
export const isLoading = (state: RootState): boolean => state[MEMBER_PLAN_SLICE_NAME].isLoading;
export const isComplete = (state: RootState): boolean => state[MEMBER_PLAN_SLICE_NAME].isComplete;
export const error = (state: RootState): string => state[MEMBER_PLAN_SLICE_NAME].error;
export const memberPlan = (state: RootState): CoverageSnapshot | null =>
  state[MEMBER_PLAN_SLICE_NAME].memberPlan;
export const planConfig = (state: RootState): PlanConfig | null =>
  state[MEMBER_PLAN_SLICE_NAME].planConfig;

/** ************** ***** ************ ************** ************** */
/** Computed values from the /init API response planConfig property */
/** ************** ***** ************ ************** ************** */
export const hasValidPlanConfig = createSelector(
  [planConfig],
  (planConfig) =>
    isFiniteNumber(planConfig?.individualDeductible) &&
    isFiniteNumber(planConfig?.individualOutOfPocketMax)
);
export const planConfigIsZeroDollarDeductible = createSelector(
  [planConfig],
  (planConfig) => planConfig?.individualDeductible === 0
);

/** ************** ************** ************** ************** */
/** Amounts and calculations from the /member-plan API response */
/** ************** ************** ************** ************** */
export const memberIndividualDeductibleAmounts = createSelector(
  [memberPlan],
  (planData: CoverageSnapshot | null): Amounts | null =>
    getPlanAmounts(planData, INDIVIDUAL_PLAN_LEVEL, DEDUCTIBLE_AMOUNT_KEY)
);
export const memberIndividualOutOfPocketMaxAmounts = createSelector(
  [memberPlan],
  (planData: CoverageSnapshot | null): Amounts | null =>
    getPlanAmounts(planData, INDIVIDUAL_PLAN_LEVEL, OOP_AMOUNT_KEY)
);
export const memberFamilyDeductibleAmounts = createSelector(
  [memberPlan],
  (planData: CoverageSnapshot | null): Amounts | null =>
    getPlanAmounts(planData, FAMILY_PLAN_LEVEL, DEDUCTIBLE_AMOUNT_KEY)
);
export const memberFamilyOutOfPocketMaxAmounts = createSelector(
  [memberPlan],
  (planData: CoverageSnapshot | null): Amounts | null =>
    getPlanAmounts(planData, FAMILY_PLAN_LEVEL, OOP_AMOUNT_KEY)
);

export const memberPlanHasDeductible = createSelector(
  [memberIndividualDeductibleAmounts],
  (individualDeductibleAmounts): boolean =>
    Boolean(individualDeductibleAmounts?.total && individualDeductibleAmounts.total > 0)
);

export const hasValidMemberPlan = createSelector(
  [
    isComplete,
    error,
    memberPlanHasDeductible,
    memberIndividualDeductibleAmounts,
    memberIndividualOutOfPocketMaxAmounts,
  ],
  (
    complete,
    error,
    hasDeductible,
    individualDeductibleAmounts,
    individualOutOfPocketMaxAmounts
  ): boolean => {
    if (error.length > 0) return false;
    if (!complete) return false;
    if (!isFiniteNumber(individualOutOfPocketMaxAmounts?.total)) return false;
    if (hasDeductible && !isFiniteNumber(individualDeductibleAmounts?.total)) return false;
    return true;
  }
);

/** ************** ************** ************** ************** */
/** Plan amounts and calculations for the Service Cost Table UI
 * These selectors take into account the validity of member plan
 * and plan config data, and return the appropriate amounts and calculations.
 *
 * Member plan amounts are given priority, plan config amounts are used as a fallback.
 */
/** ************** ************** ************** ************** */
export const planAmounts = createSelector(
  [
    isComplete,
    hasValidMemberPlan,
    memberIndividualDeductibleAmounts,
    memberIndividualOutOfPocketMaxAmounts,
    memberFamilyDeductibleAmounts,
    memberFamilyOutOfPocketMaxAmounts,
    planConfig,
    selectUi.accumulationMetStatus,
  ],
  (
    complete,
    hasValidMemberPlan,
    memberIndividualDeductibleAmounts,
    memberIndividualOutOfPocketMaxAmounts,
    memberFamilyDeductibleAmounts,
    memberFamilyOutOfPocketMaxAmounts,
    planConfig,
    accumulationMetStatus: string
  ): PlanAmounts | null => {
    if (!complete) return null;
    if (hasValidMemberPlan) {
      const amounts: PlanAmounts = {
        individualOutOfPocketMax: (memberIndividualOutOfPocketMaxAmounts as Amounts)
          .total as number, // verified as number by hasValidMemberPlan
        // allow 0 for all values
        individualDeductible: getFiniteNumber(memberIndividualDeductibleAmounts?.total),
        familyOutOfPocketMax: getFiniteNumber(memberFamilyOutOfPocketMaxAmounts?.total),
        familyDeductible: getFiniteNumber(memberFamilyDeductibleAmounts?.total),
        individualOutOfPocketMaxAccumulation: memberIndividualOutOfPocketMaxAmounts?.amount || 0,
        individualDeductibleAccumulation: memberIndividualDeductibleAmounts?.amount || 0,
        familyOutOfPocketMaxAccumulation: memberFamilyOutOfPocketMaxAmounts?.amount || 0,
        familyDeductibleAccumulation: memberFamilyDeductibleAmounts?.amount || 0,
      };
      return amounts;
    }
    if (!planConfig) return null;
    const { individualOutOfPocketMaxAccumulation, individualDeductibleAccumulation } =
      getPlanConfigAccumulations(planConfig, accumulationMetStatus);
    const planConfigAmounts: PlanAmounts = {
      individualOutOfPocketMax: planConfig.individualOutOfPocketMax,
      individualOutOfPocketMaxAccumulation,
      individualDeductible: planConfig.individualDeductible,
      individualDeductibleAccumulation,
      // family-level values are not used in /provider-cost calculations
      familyOutOfPocketMax: planConfig.familyOutOfPocketMax,
      familyDeductible: planConfig.familyDeductible,
      // family-level accumulations are not currently displayed when member plan is invalid
      familyOutOfPocketMaxAccumulation: 0,
      familyDeductibleAccumulation: 0,
    };
    return planConfigAmounts;
  }
);

export const planAmountsRemaining = createSelector(
  [planAmounts],
  (planAmounts): PlanAmountsRemaining | null => {
    if (!planAmounts) return null;
    return {
      individualOutOfPocketMaxRemaining: getRemaining(
        planAmounts.individualOutOfPocketMax,
        planAmounts.individualOutOfPocketMaxAccumulation
      ),
      individualDeductibleRemaining: getRemaining(
        planAmounts.individualDeductible,
        planAmounts.individualDeductibleAccumulation
      ),
      familyOutOfPocketMaxRemaining: getRemaining(
        planAmounts.familyOutOfPocketMax,
        planAmounts.familyOutOfPocketMaxAccumulation
      ),
      familyDeductibleRemaining: getRemaining(
        planAmounts.familyDeductible,
        planAmounts.familyDeductibleAccumulation
      ),
    };
  }
);

/** *************** */
/** Other selectors */
/** *************** */
export const insuranceName = createSelector(
  [memberPlan],
  (planData: CoverageSnapshot | null): string => planData?.payer.name || ''
);
